import { useCallback, useEffect, useRef, useState } from "react";

import {
  getGroupMemberStreams,
  getGroupMemberStreamWatch,
} from "../services/groupMemberStreamService";

const LIMIT = 12;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const getStreamId = (stream) =>
  Number(stream?.streamSessionId ?? stream?.stream_session_id);

export function useGroupMemberStream(groupTag) {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");

  const [selectedStream, setSelectedStream] = useState(null);
  const [watchData, setWatchData] = useState(null);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState("");

  const listController = useRef(null);
  const listGeneration = useRef(0);
  const nextOffset = useRef(0);
  const hasMoreRef = useRef(true);
  const pagePending = useRef(false);

  const watchController = useRef(null);
  const watchGeneration = useRef(0);

  const closeStream = useCallback(() => {
    watchController.current?.abort();
    watchController.current = null;
    watchGeneration.current += 1;

    setSelectedStream(null);
    setWatchData(null);
    setWatchLoading(false);
    setWatchError("");
  }, []);

  const load = useCallback(
    async ({ reset = false, silent = false } = {}) => {
      const tag = String(groupTag ?? "").trim();

      if (!tag) {
        setStreams([]);
        setHasMore(false);
        setLoading(false);
        setError("A valid group tag is required.");
        return false;
      }

      if (!reset && (pagePending.current || !hasMoreRef.current)) {
        return false;
      }

      listController.current?.abort();

      const controller = new AbortController();
      listController.current = controller;

      const generation = ++listGeneration.current;
      pagePending.current = true;

      if (reset) {
        nextOffset.current = 0;
        hasMoreRef.current = true;

        if (!silent) setStreams([]);

        setHasMore(false);
        setLoading(!silent);
        setRefreshing(silent);
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      setError("");

      try {
        const response = await getGroupMemberStreams(tag, {
          limit: LIMIT,
          offset: nextOffset.current,
          signal: controller.signal,
        });

        if (
          controller.signal.aborted ||
          generation !== listGeneration.current
        ) {
          return false;
        }

        const rows = response?.data;

        if (!Array.isArray(rows)) {
          throw new Error("Invalid group stream response.");
        }

        setStreams((current) => {
          if (reset) return rows;

          const seen = new Set(current.map(getStreamId));

          const additions = rows.filter((row) => !seen.has(getStreamId(row)));

          return [...current, ...additions];
        });

        nextOffset.current += rows.length;
        hasMoreRef.current = rows.length === LIMIT;

        setHasMore(hasMoreRef.current);

        return true;
      } catch (err) {
        if (
          !controller.signal.aborted &&
          generation === listGeneration.current
        ) {
          setError(getErrorMessage(err, "Unable to load group streams."));
        }

        return false;
      } finally {
        if (generation === listGeneration.current) {
          pagePending.current = false;
          setLoading(false);
          setRefreshing(false);
          setLoadingMore(false);
        }
      }
    },
    [groupTag],
  );

  useEffect(() => {
    closeStream();
    void load({ reset: true });

    return () => {
      listController.current?.abort();
      listGeneration.current += 1;
      pagePending.current = false;

      watchController.current?.abort();
      watchGeneration.current += 1;
    };
  }, [load, closeStream]);

  const refresh = useCallback(
    () => load({ reset: true, silent: true }),
    [load],
  );

  const loadMore = useCallback(() => load(), [load]);

  const openStream = useCallback(async (stream) => {
    const streamSessionId = getStreamId(stream);

    if (!Number.isSafeInteger(streamSessionId) || streamSessionId <= 0) {
      return false;
    }

    watchController.current?.abort();

    const controller = new AbortController();
    watchController.current = controller;

    const generation = ++watchGeneration.current;

    setSelectedStream(stream);
    setWatchData(null);
    setWatchError("");
    setWatchLoading(true);

    try {
      const response = await getGroupMemberStreamWatch(streamSessionId, {
        signal: controller.signal,
      });

      if (controller.signal.aborted || generation !== watchGeneration.current) {
        return false;
      }

      const data = response?.data;

      if (!data || getStreamId(data) !== streamSessionId) {
        throw new Error("Invalid stream playback response.");
      }

      setWatchData(data);

      return true;
    } catch (err) {
      if (
        !controller.signal.aborted &&
        generation === watchGeneration.current
      ) {
        setWatchError(getErrorMessage(err, "Unable to open this stream."));
      }

      return false;
    } finally {
      if (generation === watchGeneration.current) {
        setWatchLoading(false);
      }
    }
  }, []);

  return {
    streams,
    loading,
    loadingMore,
    refreshing,
    hasMore,
    error,

    refresh,
    loadMore,

    selectedStream,
    watchData,
    watchLoading,
    watchError,

    openStream,
    closeStream,
  };
}

export default useGroupMemberStream;
